"use client";
import "@/components/help/help-search.css";
import "@/components/ui/atoms/categories/data-display.css";

import "@/components/ui/atoms/categories/layout.css";
import { HELP_SEARCH_ENTRIES, type HelpSearchEntry } from "@/components/help/help-search-data";
import { Kbd } from "@/components/ui/atoms/Kbd";
import { Button } from "@/components/ui/shadcn/button";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandShortcut } from "@/components/ui/shadcn/command";

import { ArrowUpRight, Search } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";

import { memo, useEffect, useMemo, useState } from "react";

type WebMCPTool = {
    name: string;
    title: string;
    description: string;
    inputSchema: object;
    annotations: { readOnlyHint: boolean; untrustedContentHint: boolean };
    execute: (input: unknown) => unknown | Promise<unknown>;
};

type WebMCPDocument = Document & {
    modelContext?: {
        registerTool: (tool: WebMCPTool, options: { signal: AbortSignal }) => void | Promise<void>;
    };
};

function matchingHelpEntries(query: string): HelpSearchEntry[] {
    const parts = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return HELP_SEARCH_ENTRIES.filter((entry) => {
        const haystack = (entry.title + " " + entry.description + " " + entry.category + " " + (entry.keywords || []).join(" ")).toLowerCase();
        return parts.every((part) => haystack.includes(part));
    });
}

function requiredString(input: unknown, key: string): string {
    if (typeof input !== "object" || input === null || Array.isArray(input) ||
        typeof (input as Record<string, unknown>)[key] !== "string") {
        throw new Error(`Expected ${key} to be a string.`);
    }
    return (input as Record<string, string>)[key];
}

export const HelpSearch = memo(function HelpSearch() {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const router = useRouter();
    const searchParams = useSearchParams();

    useEffect(() => {
        const context = (document as WebMCPDocument).modelContext;
        if (!context?.registerTool) return;

        const lifecycle = new AbortController();
        const register = (tool: WebMCPTool) => {
            try {
                void Promise.resolve(context.registerTool(tool, { signal: lifecycle.signal })).catch(console.error);
            } catch (error) {
                console.error(error);
            }
        };

        register({
            name: "search_disputewell_help",
            title: "Search DisputeWell help",
            description: "Find Help Center articles by words in their title, description, category, or keywords. Returns every matching article without changing the page.",
            inputSchema: {
                type: "object",
                properties: { query: { type: "string", maxLength: 200 } },
                required: ["query"],
                additionalProperties: false,
            },
            annotations: { readOnlyHint: true, untrustedContentHint: false },
            execute(input) {
                const query = requiredString(input, "query");
                if (query.length > 200) throw new Error("Query must be at most 200 characters.");
                return matchingHelpEntries(query).map(({ title, description, category, href }) => ({ title, description, category, href }));
            },
        });

        register({
            name: "open_disputewell_help_article",
            title: "Open a Help Center article",
            description: "Navigate to one of the DisputeWell Help Center article paths returned by search_disputewell_help.",
            inputSchema: {
                type: "object",
                properties: { href: { type: "string", description: "An exact article path returned by search_disputewell_help." } },
                required: ["href"],
                additionalProperties: false,
            },
            annotations: { readOnlyHint: false, untrustedContentHint: false },
            async execute(input) {
                const href = requiredString(input, "href");
                if (!HELP_SEARCH_ENTRIES.some((entry) => entry.href === href)) {
                    throw new Error("Choose an article path from the Help Center search results.");
                }
                if (window.location.pathname !== href) router.push(href);
                await new Promise<void>((resolve, reject) => {
                    const deadline = Date.now() + 10000;
                    const check = () => {
                        if (window.location.pathname === href) resolve();
                        else if (Date.now() >= deadline) reject(new Error("Help article navigation did not complete."));
                        else window.setTimeout(check, 50);
                    };
                    check();
                });
                return { href, status: "opened" };
            },
        });

        return () => lifecycle.abort();
    }, [router]);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
                event.preventDefault();
                setOpen((current) => !current);
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    // The CRM/portal/admin shells' `global-focus-search` shortcut (mod+/)
    // navigates here with `?search=1` since no in-shell help-search input
    // exists to focus directly (src/lib/shortcuts/registry.ts). Opening the
    // dialog autofocuses its `<CommandInput autoFocus />` (below). Strip the
    // param afterward so back/forward navigation doesn't re-trigger it.
    const searchParamRequested = searchParams.get("search") === "1";

    // `?search=1` is an edge, not a state: open on the transition into it, using
    // React's documented previous-value comparison during render. `sawSearchParam`
    // starts false so a first paint that already carries the param counts as a
    // transition, and it falls back to false once router.replace below strips the
    // param, re-arming the shortcut for the next press without a remount.
    const [sawSearchParam, setSawSearchParam] = useState(false);
    if (searchParamRequested !== sawSearchParam) {
        setSawSearchParam(searchParamRequested);
        if (searchParamRequested) {
            setOpen(true);
        }
    }

    useEffect(() => {
        if (searchParams.get("search") !== "1") {
            return;
        }
        const params = new URLSearchParams(searchParams);
        params.delete("search");
        const remaining = params.toString();
        router.replace(remaining ? `/help?${remaining}` : "/help", { scroll: false });
    }, [searchParams, router]);

    const filteredEntries = useMemo(() => matchingHelpEntries(query), [query]);

    const groupedEntries = useMemo(() => {
        const groups: Record<string, HelpSearchEntry[]> = {};
        filteredEntries.forEach((entry) => {
            if (!groups[entry.category]) {
                groups[entry.category] = [];
            }
            groups[entry.category].push(entry);
        });
        return groups;
    }, [filteredEntries]);

    const handleSelect = (href: string) => {
        setOpen(false);
        setQuery("");
        router.push(href);
    };

    return (
        <>
            <Button type="button" variant="outline" onClick={() => setOpen(true)} className="dw-help-search__trigger">
                <Search className="dw-help-search__icon dw-icon-box" data-tone="muted" />
                <span className="dw-help-search__placeholder">Search docs…</span>
                <span className="dw-help-search__kbd"><Kbd>⌘</Kbd><Kbd>K</Kbd></span>
            </Button>

            <CommandDialog
                open={open}
                onOpenChange={(isOpen) => {
                    setOpen(isOpen);
                    if (!isOpen) {
                        setQuery("");
                    }
                }}>
                <CommandInput placeholder="Search help and documentation…" value={query} onValueChange={setQuery} autoFocus/>
                <CommandList>
                    <CommandEmpty>No results found</CommandEmpty>
                    {Object.entries(groupedEntries).map(([category, entries]) => (
                        <CommandGroup heading={category} key={category}>
                            {entries.map((entry) => (
                                <CommandItem
                                    key={entry.href}
                                    value={entry.title}
                                    onSelect={() => handleSelect(entry.href)}>
                                    <div className="dw-help-search__result">
                                        <span className="dw-text-label" data-tone="foreground">{entry.title}</span>
                                        <span className="dw-text-caption" data-tone="muted">{entry.description}</span>
                                    </div>
                                    <CommandShortcut>
                                        <ArrowUpRight className="dw-help-search__icon dw-icon-box" data-tone="muted" />
                                    </CommandShortcut>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    ))}
                </CommandList>
            </CommandDialog>
        </>
    );
});
